import { CommonModule } from '@angular/common';
import { Component, OnInit, effect, inject, signal, untracked } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';
import { ToastService } from 'angular-toastify';
import { PssConfirmModalComponent } from '../../../../tools/template/pss/pss-confirm-modal/pss-confirm-modal.component';
import { PssLoadingComponent } from '../../../../tools/template/pss/pss-loading/pss-loading.component';
import { CardTypeConfigListRow } from '../../../../core/models/card-quota/card-type-config.model';
import { MapperCardTypeConfigService } from '../../../../core/mappers/card-quota/mapper-card-type-config.service';
import { ApiCardQuotaService } from '../../../../core/services/api/card-quota/api-card-quota.service';
import { MenuPermissionService } from '../../../../core/services/menu-permission.service';
import { SiteStateService } from '../../../../core/services/site-state.service';
import { ModalCardTypeConfigComponent } from '../../modals/card-quota/modal-card-type-config/modal-card-type-config.component';

@Component({
  selector: 'app-setting-card-type-config',
  standalone: true,
  imports: [CommonModule, FormsModule, PssConfirmModalComponent, PssLoadingComponent, ModalCardTypeConfigComponent],
  templateUrl: './setting-card-type-config.component.html',
  styleUrl: './setting-card-type-config.component.css',
})
export class SettingCardTypeConfigComponent implements OnInit {
  readonly siteState = inject(SiteStateService);
  private readonly api = inject(ApiCardQuotaService);
  private readonly mapper = inject(MapperCardTypeConfigService);
  private readonly toast = inject(ToastService);
  private readonly router = inject(Router);
  private readonly permissionService = inject(MenuPermissionService);
  private readonly cookieService = inject(CookieService);

  items = signal<CardTypeConfigListRow[]>([]);
  isLoading = signal(false);
  isModalOpen = signal(false);
  selectedConfigId = signal(0);
  isDeleteConfirmOpen = signal(false);
  deleteTarget = signal<CardTypeConfigListRow | null>(null);

  currentPage = signal(1);
  pageSize = signal(20);
  totalItems = signal(0);
  totalPages = signal(1);
  filterEnable = signal<'all' | 'true' | 'false'>('all');

  constructor() {
    effect(() => {
      const site = this.siteState.selectedSite();
      if (!site?.id) return;
      untracked(() => {
        this.currentPage.set(1);
        this.loadTable();
      });
    });
  }

  get selectedSiteId(): number {
    return this.siteState.selectedSite()?.id ?? 0;
  }

  get canCreate(): boolean {
    return this.permissionService.canCreate(this.router.url);
  }

  get canUpdate(): boolean {
    return this.permissionService.canUpdate(this.router.url);
  }

  get canDelete(): boolean {
    return this.permissionService.canDelete(this.router.url);
  }

  get selectedSiteName(): string {
    return this.siteState.selectedSite()?.name ?? '';
  }

  get startItemIndex(): number {
    if (this.totalItems() === 0) return 0;
    return (this.currentPage() - 1) * this.pageSize() + 1;
  }

  get endItemIndex(): number {
    return Math.min(this.currentPage() * this.pageSize(), this.totalItems());
  }

  ngOnInit(): void {
    if (this.selectedSiteId) this.loadTable();
  }

  scopeLabel = (scope: string) => this.mapper.scopeLabel(scope);

  onRefresh(): void {
    this.currentPage.set(1);
    this.loadTable();
  }

  onFilterChange(): void {
    this.currentPage.set(1);
    this.loadTable();
  }

  openCreate(): void {
    if (!this.canCreate) {
      this.toast.error('ไม่มีสิทธิ์สร้าง');
      return;
    }
    if (!this.selectedSiteId) {
      this.toast.error('กรุณาเลือกลานจาก Sidebar ก่อนสร้าง whitelist');
      return;
    }
    this.selectedConfigId.set(0);
    this.isModalOpen.set(true);
  }

  openEdit(item: CardTypeConfigListRow): void {
    if (!this.canUpdate) {
      this.toast.error('ไม่มีสิทธิ์แก้ไข');
      return;
    }
    this.selectedConfigId.set(item.id);
    this.isModalOpen.set(true);
  }

  openDeleteConfirm(item: CardTypeConfigListRow): void {
    if (!this.canDelete) {
      this.toast.error('ไม่มีสิทธิ์ลบ');
      return;
    }
    this.deleteTarget.set(item);
    this.isDeleteConfirmOpen.set(true);
  }

  closeDeleteConfirm(): void {
    this.isDeleteConfirmOpen.set(false);
    this.deleteTarget.set(null);
  }

  confirmDelete(): void {
    const item = this.deleteTarget();
    if (!item) return;

    const userId = this.resolveUserId();
    const menuId = this.permissionService.getMenuId(this.router.url) ?? 0;
    if (!userId || !menuId) {
      this.toast.error('ไม่พบข้อมูลผู้ใช้หรือสิทธิ์เมนู');
      this.closeDeleteConfirm();
      return;
    }

    this.closeDeleteConfirm();
    this.isLoading.set(true);

    this.api.deleteCardTypeConfig(item.id, userId, menuId).subscribe({
      next: (res) => {
        const mapped = this.mapper.mapActionResponse(res);
        if (mapped.isSuccess) {
          this.toast.success(mapped.message || 'ลบสำเร็จ');
          this.loadTable();
        } else {
          this.toast.error(mapped.message || 'ลบไม่สำเร็จ');
          this.isLoading.set(false);
        }
      },
      error: () => {
        this.toast.error('เกิดข้อผิดพลาด');
        this.isLoading.set(false);
      },
    });
  }

  closeModal(): void {
    this.isModalOpen.set(false);
    this.selectedConfigId.set(0);
  }

  setPage(page: number): void {
    if (page >= 1 && page <= this.totalPages()) {
      this.currentPage.set(page);
      this.loadTable();
    }
  }

  trackById(_index: number, item: CardTypeConfigListRow): number {
    return item.id;
  }

  get pagesArray(): number[] {
    const total = this.totalPages();
    const maxVisible = 5;
    let start = Math.max(1, this.currentPage() - 2);
    let end = Math.min(total, start + maxVisible - 1);
    if (end - start < maxVisible - 1) start = Math.max(1, end - maxVisible + 1);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  }

  loadTable(): void {
    const siteId = this.selectedSiteId;
    if (!siteId) {
      this.items.set([]);
      return;
    }

    this.isLoading.set(true);
    const enable = this.filterEnable() === 'all' ? null : this.filterEnable() === 'true';

    this.api.getCardTypeConfigsForTable(this.currentPage(), this.pageSize(), siteId, enable).subscribe({
      next: (res) => {
        const mapped = this.mapper.mapTable(res);
        this.items.set(mapped.items);
        this.totalItems.set(mapped.total);
        this.totalPages.set(mapped.totalPages || Math.max(1, Math.ceil(mapped.total / this.pageSize())));
        this.isLoading.set(false);
      },
      error: () => {
        this.items.set([]);
        this.isLoading.set(false);
        this.toast.error('โหลดข้อมูลล้มเหลว');
      },
    });
  }

  private resolveUserId(): number {
    const fromLocal = localStorage.getItem('userId');
    if (fromLocal) return Number(fromLocal);
    return Number(this.cookieService.get('user_id')) || 0;
  }
}
